<template>
  <div class="purchase-details">
    <div class="margin-bottom-xs">
      <BreadcrumbWrapper :current-page="currentPage" />
    </div>
  </div>

  <div class="grid-x">
    <div class="cell small-12 medium-10">
      <h1 class="margin-bottom-m cell">
        {{ PURCHASE_DETAILS.title }}
      </h1>
      <div v-if="selectedShipment">
        <h4 class="margin-bottom-xxs">
          {{ shipmentStatus }}
        </h4>
        <div class="margin-bottom-xxs">
          <span class="price-reg"> {{ selectedShipment.shipmentTotal }}, </span> {{ selectedShipment.shipmentConfirmationDate }}
        </div>
        <div>
          {{ selectedShipment.storeName }}
        </div>
      </div>
      <div class="margin-bottom-m">
        <template v-if="shipmentStatus === 'Exchange'">
          <h5 class="margin-top-m">
            {{ PURCHASE_DETAILS.purchasedText }}
          </h5>
          <ProductThumbnailWrapper :products="purchasedItems" hide-item-border page="purchase-details" />
          <h5 class="margin-top-m">
            {{ PURCHASE_DETAILS.returnedText }}
          </h5>
          <ProductThumbnailWrapper :products="returnedItems" hide-item-border page="purchase-details" />
        </template>
        <ProductThumbnailWrapper v-else :products="shipmentItems" hide-item-border />
      </div>
      <div v-if="shipmentStatus === 'Purchased'" class="grid-x align-center align-middle cell margin-bottom-xxl">
        <div class="barcode-section large-padding-left-xs large-padding-right-xs text-center medium">
          <div class="margin-bottom-xs">
            {{ PURCHASE_DETAILS.barcodeMessage }}
          </div>

          <div class="margin-bottom-xs">
            <img class="margin-bottom-xs" alt="Barcode" :src="fetchBarCodeImage" />

            <div v-if="selectedShipment?.receiptIdentifier" v-text="selectedShipment?.receiptIdentifier"></div>
          </div>
          <NuxtLink :to="RETURN_CENTER_TEXT.returnFaqs.url" external target="_blank">{{ PURCHASE_DETAILS.returnPolicyText }}</NuxtLink>
        </div>
      </div>
    </div>
    <div class="cell small-12 medium-2 padding-left-l">
      <ProsWrapper v-if="storeItems.length" data-context="PR_ZONE_A" :killswitches="{ disableApisWithUserIdInPayload: true }" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTitle } from '@vueuse/core';
import { UserStatus } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import ProductThumbnailWrapper from '@/components/common/ProductThumbnailWrapper';
import ProsWrapper from '@/components/ProsWrapper';
import { PURCHASE_DETAILS, RETURN_CENTER_TEXT, brandName } from 'config';
import { useInstoreOrderHistoryStore } from '@/stores/ppe/useInstoreOrderHistoryStore';

const router = useRouter();
const route = useRoute();

const instoreOrderHistoryStore = useInstoreOrderHistoryStore();

const currentPage = ref('Purchase Details');

const selectedShipment = computed(() => instoreOrderHistoryStore.selectedShipment);
const shipmentStatus = computed(() => selectedShipment.value?.shipmentStatus);
const fetchBarCodeImage = computed(
  () => `/api/ordermodsreturns/v1/returns/barcodegenerator?no=${route.params.receiptNumber}&format=PRINT128&add_hex=false`,
);
const storeItems = computed(() => {
  const items = [];
  selectedShipment.value?.items?.forEach((item) => {
    if (item) {
      items.push({ productId: item.product?.id });
    }
  });
  return items;
});
const transformItems = (items = []) =>
  items.map((item) => {
    if (item && Object.keys(item).length) {
      const itemProduct = JSON.parse(JSON.stringify(item.product));
      itemProduct.detail.name =
        (item.vendorName ? `${item.vendorName} ${item.itemDescription}` : item.itemDescription) || PURCHASE_DETAILS.defaultItemErrorMessage;
      itemProduct.detail.brand = item.product?.detail?.brand || (itemProduct.detail.name ? 'Brand Name' : 'Item Name');
      itemProduct.detail.colorAndSize = [...(itemProduct?.traits?.size || ''), ...(itemProduct?.traits?.colors || '')];
      itemProduct.detail.quantity = item.currentQuantity;
      if (itemProduct?.price?.length === 1) {
        itemProduct.pricing = {
          price: {
            tieredPrice: [
              {
                label: '[PRICE]',
                values: [
                  {
                    type: 'regular',
                    ...itemProduct?.price[0]?.values[0],
                  },
                ],
              },
            ],
          },
        };
      }
      if (itemProduct.id) {
        itemProduct.identifier = { productUrl: `/shop/product/?ID=${itemProduct.id}` };
      }
      return itemProduct;
    }
    return {};
  });
const getItems = (itemType) =>
  computed(() => {
    if (!selectedShipment.value) return [];
    return transformItems(selectedShipment.value[itemType]);
  });
const purchasedItems = getItems('purchasedItems');
const returnedItems = getItems('returnedItems');
const shipmentItems = getItems('items');

onMounted(() => {
  useTitle(`${PURCHASE_DETAILS.pageTitle} - ${brandName}`);
  fireTag('view', {
    page_name: PURCHASE_DETAILS.tag.page_name,
    page_type: PURCHASE_DETAILS.tag.page_type,
    customer_type: UserStatus.isSignedIn() ? 'signed in' : 'guest',
  });
  if (!selectedShipment.value && window?.history?.state?.back?.includes('purchases/store')) {
    router.back();
  } else if (!selectedShipment.value && !window?.history?.state?.back) {
    router.replace({ name: 'in-store-history' });
  }
});
onUnmounted(() => {
  instoreOrderHistoryStore.CLEAR_SELECTED_SHIPMENT();
});
</script>

<style lang="scss" scoped>
.barcode-section {
  max-width: rem-calc(410);
  &img {
    width: rem-calc(200);
  }
}
</style>
